/**
 * Tiny SVG ward map for onboarding (screenshots/101): projects the City's ward boundaries
 * (public/geo/chicago-wards.json, built by scripts/build-ward-geo.mjs from dataset p293-wvbd) into a box.
 * Equirectangular with a cos(latitude) correction is accurate enough at city scale.
 */
type Ring = [number, number][];
export interface WardFeature { properties: { ward: string | number }; geometry: { type: "Polygon"; coordinates: Ring[] } | { type: "MultiPolygon"; coordinates: Ring[][] } }
export interface WardGeo { features: WardFeature[] }

const rings = (f: WardFeature): Ring[] => (f.geometry.type === "Polygon" ? f.geometry.coordinates : f.geometry.coordinates.flat());

export function wardPaths(geo: WardGeo, width: number, height: number, pad = 2): { ward: number; d: string }[] {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const f of geo.features) for (const r of rings(f)) for (const [x, y] of r) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
  const k = Math.cos(((minY + maxY) / 2) * Math.PI / 180);
  const sx = (maxX - minX) * k, sy = maxY - minY;
  const s = Math.min((width - 2 * pad) / sx, (height - 2 * pad) / sy);
  const ox = (width - sx * s) / 2, oy = (height - sy * s) / 2;
  const P = ([x, y]: [number, number]) => `${(ox + (x - minX) * k * s).toFixed(1)} ${(oy + (maxY - y) * s).toFixed(1)}`;
  return geo.features.map((f) => ({ ward: Number(f.properties.ward), d: rings(f).map((r) => `M${r.map(P).join("L")}Z`).join("") }))
    .sort((a, b) => a.ward - b.ward);
}
